import { inject, Injectable } from '@angular/core';
import { SQLite, SQLiteObject } from '@awesome-cordova-plugins/sqlite/ngx';
import * as moment from 'moment';
import { DatabaseIndice, DatabaseRelation } from '../interfaces/database';
import { Capacitor } from '@capacitor/core';
import { Paginator } from '../interfaces/paginator';
import { Observable, catchError, forkJoin, from, map, of } from 'rxjs';
import { StorageService } from './storage.service';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';

declare global {
    interface Window {
        openDatabase?: any;
    }
}

@Injectable({
    providedIn: 'root'
})
export abstract class DatabaseService {

    abstract classname: string;

    public static DB: SQLiteObject;
    private sqlite = new SQLite();

    /**
     * Default: tabelas normais continuam sincronizando pelo id do servidor.
     * Para tabelas criadas offline, sobrescreva no DB específico:
     * override keyPath = 'uuid';
     * override typeKey = 'TEXT';
     */
    public table!: string;
    public keyPath = 'id';
    protected typeKey = 'INTEGER';
    protected autoIncrement = false;
    protected sincIndex = 0; // Mantido por compatibilidade com DB services antigos; não é mais usado na sync por UUID/id.

    protected uuidField = 'uuid';
    protected useUuidKey = false;

    public indices: DatabaseIndice[] = [];
    protected initialized = false;
    protected relations: DatabaseRelation[] = [];

    //CACHE DE RELACOES JA BUSCADAS
    private relationCache: Map<string, any> = new Map();

    protected migration = 0;
    protected migrationChunkSize = 200;
    protected storage: StorageService = inject(StorageService);

    constructor(){}

    private static patchSqlDebug(db: any): any{
        if(environment.production || !db || db.__ebrigadaSqlDebugPatched){
            return db;
        }

        if(typeof db.executeSql !== 'function'){
            return db;
        }

        const originalExecuteSql = db.executeSql.bind(db);

        Object.defineProperty(db, '__ebrigadaSqlDebugPatched', {
            value: true,
            enumerable: false,
            configurable: false,
            writable: false,
        });

        db.executeSql = async (statement: string, params: any[] | undefined = undefined) => {
            try{
                return await originalExecuteSql(statement, params);
            }catch(error){
                console.error('[SQLite] Erro ao executar SQL', {
                    sql: statement,
                    params,
                    error,
                });

                throw error;
            }
        };

        return db;
    }

    protected async open(){
        if(Capacitor.isNativePlatform()){
            DatabaseService.DB = await this.sqlite.create({
                name: 'ebrigada.db',
                location: 'default'
            });

            DatabaseService.patchSqlDebug(DatabaseService.DB);
            return DatabaseService.DB;
        }

        const DB = new DataBaseLocal();
        DatabaseService.DB = new LocalDB(DB) as unknown as any;
        DatabaseService.patchSqlDebug(DatabaseService.DB);
        return of(DatabaseService.DB).toPromise();
    }

    private async getTableColumns(): Promise<Set<string>>{
        const columns = new Set<string>();

        try{
            const result = await DatabaseService.DB.executeSql('PRAGMA table_info(' + this.table + ')', []);

            for(let i = 0; i < result.rows.length; i++){
                const name = result.rows.item(i).name;

                if(name){
                    columns.add(name);
                }
            }
        }catch(_){}

        return columns;
    }

    private getColumnDefinition(index: DatabaseIndice, forAlter = false): string{
        const notNull = index.notNull && (!forAlter || index.default);
        return index.name + ' VARCHAR(255) ' + (notNull ? 'NOT NULL ' : '') + (index.default ? 'DEFAULT ' + index.default : '');
    }

    private async ensureIndexColumns(): Promise<boolean>{
        const columns = await this.getTableColumns();
        let changed = false;

        for(const index of this.indices){
            if(index.multiEntry || columns.has(index.name)){
                continue;
            }

            await DatabaseService.DB.executeSql('ALTER TABLE ' + this.table + ' ADD COLUMN ' + this.getColumnDefinition(index, true), []);
            columns.add(index.name);
            changed = true;
        }

        return changed;
    }

    private async backfillIndexColumns(): Promise<void>{
        const simpleIndices = this.indices.filter((index) => !index.multiEntry);

        if(simpleIndices.length === 0){
            return;
        }

        let lastRowId = 0;

        while(true){
            const result = await DatabaseService.DB.executeSql(
                'SELECT rowid as __rowid, value FROM ' + this.table + ' WHERE rowid > ? ORDER BY rowid LIMIT ?',
                [lastRowId, this.migrationChunkSize]
            );

            if(result.rows.length === 0){
                break;
            }

            for(let i = 0; i < result.rows.length; i++){
                const row = result.rows.item(i);
                lastRowId = Number(row.__rowid);

                try{
                    const item = JSON.parse(row.value);
                    const values = simpleIndices.map((index) => {
                        const value = this.getProperty(item, index.field);
                        return this.hasValue(value) ? value.toString() : (index.default ?? null);
                    });

                    await DatabaseService.DB.executeSql(
                        'UPDATE ' + this.table + ' SET ' + simpleIndices.map((index) => index.name + ' = ?').join(', ') + ' WHERE rowid = ?',
                        [...values, lastRowId]
                    );
                }catch(_){}
            }
        }
    }

    private async backfillMultiEntryIndices(): Promise<void>{
        if(!this.indices.some((index) => index.multiEntry)){
            return;
        }

        let lastRowId = 0;

        while(true){
            const result = await DatabaseService.DB.executeSql(
                'SELECT rowid as __rowid, ' + this.keyPath + ' as __key, value FROM ' + this.table + ' WHERE rowid > ? ORDER BY rowid LIMIT ?',
                [lastRowId, this.migrationChunkSize]
            );

            if(result.rows.length === 0){
                break;
            }

            const items: any[] = [];

            for(let i = 0; i < result.rows.length; i++){
                const row = result.rows.item(i);
                lastRowId = Number(row.__rowid);

                try{
                    const item = JSON.parse(row.value);
                    item[this.keyPath] = item[this.keyPath] ?? row.__key;
                    items.push(item);
                }catch(_){}
            }

            await this.syncMultiEntryIndices(items);
        }
    }

    private async migrate(): Promise<any>{
        const migration = await this.storage.get('migration') || {};
        migration[this.table] = migration[this.table] || 0;

        const schemaChanged = await this.ensureIndexColumns();

        if(migration[this.table] == this.migration && !schemaChanged){
            await this.storage.set('migration', migration);
            return;
        }

        await this.backfillIndexColumns();
        await this.backfillMultiEntryIndices();
        migration[this.table] = this.migration;
        await this.storage.set('migration', migration);
    }

    async createTable(): Promise<any>{
        if(!DatabaseService.DB){
            await this.open();
            return this.createTable();
        }

        DatabaseService.patchSqlDebug(DatabaseService.DB);

        let sql = 'CREATE TABLE IF NOT EXISTS ' + this.table + ' (';
        sql += this.keyPath + ' ' + this.typeKey + ' PRIMARY KEY ' + (this.autoIncrement && this.typeKey == 'INTEGER' ? ' AUTOINCREMENT' : '');
        sql += ',value TEXT NOT NULL';

        for(const index of this.indices){
            if(index.multiEntry){
                continue;
            }

            sql += ',' + index.name + ' VARCHAR(255) ' + (index.notNull ? 'NOT NULL ' : '') + (index.default ? 'DEFAULT ' + index.default : '');
        }

        sql += ')';

        const result = await DatabaseService.DB.executeSql(sql, []);

        for(const index of this.indices){
            if(index.multiEntry){
                await this.createMultiEntryIndex(index);
            }
        }

        await this.migrate();

        for(const index of this.indices){
            if(index.multiEntry){
                continue;
            }

            const indexName = index.unique
                ? this.table + '_' + index.field.replace(/\W+/g, '_') + '_unique_index'
                : this.table + '_' + index.name + '_index';

            const query = 'CREATE ' + (index.unique ? 'UNIQUE ' : '') + 'INDEX IF NOT EXISTS ' + indexName + ' ON ' + this.table + '(' + index.name + ')';
            await DatabaseService.DB.executeSql(query, []);
        }

        this.initialized = true;
        return result;
    }

    private async createMultiEntryIndex(index: DatabaseIndice): Promise<void>{
        const query = 'CREATE TABLE IF NOT EXISTS ' + this.table + '_' + index.name + ' (' +
            index.name + ' VARCHAR(255) NOT NULL, ' +
            'foreign_' + this.keyPath + ' VARCHAR(255) NOT NULL, ' +
            'PRIMARY KEY(' + index.name + ', foreign_' + this.keyPath + ')' +
            ')';

        await DatabaseService.DB.executeSql(query, []);
        await DatabaseService.DB.executeSql(
            'CREATE INDEX IF NOT EXISTS ' + this.table + '_' + index.name + '_foreign_' + this.keyPath + '_index ON ' + this.table + '_' + index.name + '(foreign_' + this.keyPath + ')',
            []
        );
    }

    async dropTable(): Promise<any>{
        if(!DatabaseService.DB){
            await this.open();
            return this.dropTable();
        }

        this.initialized = false;

        for(const index of this.indices){
            if(index.multiEntry){
                await DatabaseService.DB.executeSql('DROP TABLE IF EXISTS ' + this.table + '_' + index.name, []);
            }
        }

        return DatabaseService.DB.executeSql('DROP TABLE IF EXISTS ' + this.table, []);
    }

    protected shouldUseUuid(): boolean{
        return this.useUuidKey || this.keyPath === this.uuidField;
    }

    protected ensureUuid(obj: any): any{
        if(!obj || !this.shouldUseUuid()){
            return obj;
        }

        if(!obj[this.uuidField]){
            obj[this.uuidField] = uuidv4();
        }

        return obj;
    }

    protected ensurePrimaryKey(obj: any): any{
        if(!obj){
            return obj;
        }

        this.ensureUuid(obj);

        if(this.shouldUseUuid() && !obj[this.keyPath] && obj[this.uuidField]){
            obj[this.keyPath] = obj[this.uuidField];
        }

        return obj;
    }

    async getAll(params: any = null, headers: any = null): Promise<Paginator>{
        if(!this.initialized){
            await this.createTable();
            return this.getAll(params, headers);
        }

        let sql = 'SELECT * FROM ' + this.table;
        const objects: any[] = [];

        if(params){
            if(params.edificacao_id && this.indices.find((i) => i.field.indexOf('edificacao_id') != -1)){
                sql += ' WHERE by_edificacao = ' + params.edificacao_id.toString();
            }

            sql = this.filterSql(sql, params);

            if(params.order && this.indices.find((i) => i.field.indexOf(params.order.split(' ')[0]) != -1)){
                sql += ' ORDER BY ' + this.indices.find((i) => i.field.indexOf(params.order.split(' ')[0]) != -1)?.name + ' ' + params.order.split(' ')[1];
            }
        }

        const r = await DatabaseService.DB.executeSql(sql, []);

        for(let i = 0; i < r.rows.length; i++){
            objects.push(this.defineObj(JSON.parse(r.rows.item(i).value)));
        }

        // const withObjects = await Promise.all(objects.map((o) => this.with(o)));
        const withObjects = await this.withBatch(objects);

        return {
            current_page: 1,
            data: withObjects,
            next_page_url: null
        };
    }

    async getByKey(key: number | string, index: string | null = null): Promise<any>{
        if(!this.initialized){
            await this.createTable();
            return this.getByKey(key, index);
        }

        let sql = 'SELECT value FROM ' + this.table + ' WHERE ';
        sql += index ? index + ' = ?' : this.keyPath + ' = ?';

        const result = await DatabaseService.DB.executeSql(sql, [key]);

        if(result.rows.length > 0){
            return this.with(this.defineObj(JSON.parse(result.rows.item(0).value)));
        }

        throw new Error('Registro não encontrado em ' + this.table + ' por ' + (index || this.keyPath) + ' = ' + key);
    }

    protected async with(object: any): Promise<any>{
        const observers: Observable<any>[] = [];

        this.relations.forEach((rel) => {
            if(this.hasRelationLoaded(object, rel.name)){
                return;
            }

            if(rel.type == 'foreign'){
                const query = 'SELECT value FROM ' + rel.table + ' WHERE by_id = ?';
                const param = object[rel.field] ? [object[rel.field].toString()] : [''];

                observers.push(
                    from(DatabaseService.DB.executeSql(query, param)).pipe(
                        map((r: any) => {
                            if(r.rows.length > 0){
                                object[rel.name] = JSON.parse(r.rows.item(0).value);
                            }

                            return object;
                        }),
                        catchError(() => of(object))
                    )
                );
            }
        });

        if(observers.length > 0){
            await new Promise((resolve) => forkJoin(observers).subscribe(resolve));
        }

        return object;
    }

    private splitInChunks<T>(array: T[], chunkSize: number): T[][]{
        const chunks: T[][] = [];

        for(let i = 0; i < array.length; i += chunkSize){
            chunks.push(array.slice(i, i + chunkSize));
        }

        return chunks;
    }

    private hasValue(value: any): boolean{
        return value !== undefined && value !== null && value !== '';
    }

    private getRelationCacheKey(table: string, key: any): string{
        return table + ':' + key.toString();
    }

    private hasRelationLoaded(object: any, relationName: string): boolean{
        return Object.prototype.hasOwnProperty.call(object, relationName);
    }

    private async withBatch(objects: any[]): Promise<any[]>{
        if(!objects.length || !this.relations.length){
            return objects;
        }

        for(const rel of this.relations){
            if(rel.type !== 'foreign'){
                continue;
            }

            /**
             * Primeiro coleta somente os objetos que ainda NÃO têm a relação no JSON.
             * Se já veio equipamento, andar, usuario, etc. no value, não consulta de novo.
             */
            const objectsWithoutRelation = objects.filter((object) => {
                return !this.hasRelationLoaded(object, rel.name);
            });

            if(objectsWithoutRelation.length === 0){
                continue;
            }

            /**
             * Coleta os IDs necessários para essa relação.
             * Exemplo: atividade.equipamento_id, atividade.usuario_id, atividade.andar_id.
             */
            const relationIds = Array.from(new Set(
                objectsWithoutRelation
                    .map((object) => this.getProperty(object, rel.field))
                    .filter((value) => this.hasValue(value))
                    .map((value) => value.toString())
            ));

            if(relationIds.length === 0){
                continue;
            }

            const relatedById = new Map<string, any>();
            const idsToFetch: string[] = [];

            /**
             * Primeiro tenta reaproveitar o cache em memória.
             */
            for(const id of relationIds){
                const cacheKey = this.getRelationCacheKey(rel.table, id);
                const cached = this.relationCache.get(cacheKey);

                if(cached !== undefined){
                    relatedById.set(id, cached);
                }else{
                    idsToFetch.push(id);
                }
            }

            /**
             * Busca no SQLite somente o que ainda não estava em cache.
             */
            if(idsToFetch.length > 0){
                const chunks = this.splitInChunks(idsToFetch, 400);

                for(const chunk of chunks){
                    const placeholders = chunk.map(() => '?').join(',');
                    const query = 'SELECT by_id, value FROM ' + rel.table + ' WHERE by_id IN (' + placeholders + ')';
                    const result = await DatabaseService.DB.executeSql(query, chunk);

                    for(let i = 0; i < result.rows.length; i++){
                        const row = result.rows.item(i);

                        if(!this.hasValue(row.by_id)){
                            continue;
                        }

                        const id = row.by_id.toString();
                        const related = JSON.parse(row.value);

                        relatedById.set(id, related);
                        this.relationCache.set(this.getRelationCacheKey(rel.table, id), related);
                    }
                }
            }

            /**
             * Aplica a relação nos objetos originais.
             */
            objectsWithoutRelation.forEach((object) => {
                const relationId = this.getProperty(object, rel.field);

                if(!this.hasValue(relationId)){
                    return;
                }

                const related = relatedById.get(relationId.toString());

                if(related){
                    object[rel.name] = related;
                }
            });
        }

        return objects;
    }

    private prepareSyncArray(array: any[]): any[]{
        const uniqueByKey = new Map<string, any>();
        const withoutKey: any[] = [];

        array.forEach((item) => {
            if(!item){
                return;
            }

            const object = this.ensurePrimaryKey({...item});
            const key = object[this.keyPath];

            if(!this.hasValue(key)){
                withoutKey.push(object);
                return;
            }

            uniqueByKey.set(key.toString(), object);
        });

        return [...uniqueByKey.values(), ...withoutKey];
    }

    private buildFields(): string[]{
        return [
            this.keyPath,
            'value',
            ...this.indices.filter((index) => !index.multiEntry).map((index) => index.name)
        ];
    }

    private buildValues(item: any, value: string): any[]{
        const values: any[] = [item[this.keyPath], value];

        this.indices.forEach((index) => {
            if(index.multiEntry){
                return;
            }

            const valor = this.getProperty(item, index.field);
            values.push(this.hasValue(valor) ? valor.toString() : (index.default ?? null));
        });

        return values;
    }

    private multiEntryValues(item: any, index: DatabaseIndice): string[]{
        const raw = this.getProperty(item, index.field);
        const rawValues: any[] = Array.isArray(raw) ? raw : [raw];
        const values = rawValues
            .filter((value: any) => this.hasValue(value))
            .map((value: any) => value.toString());

        return Array.from(new Set(values));
    }

    private async syncMultiEntryIndices(items: any[]): Promise<void>{
        const keyedItems = items.filter((item) => this.hasValue(item?.[this.keyPath]));

        if(keyedItems.length === 0){
            return;
        }

        for(const index of this.indices.filter((item) => item.multiEntry)){
            const foreignField = 'foreign_' + this.keyPath;
            const keys = Array.from(new Set(keyedItems.map((item) => item[this.keyPath].toString())));

            for(const keyChunk of this.splitInChunks(keys, 400)){
                const placeholders = keyChunk.map(() => '?').join(',');
                await DatabaseService.DB.executeSql(
                    'DELETE FROM ' + this.table + '_' + index.name + ' WHERE ' + foreignField + ' IN (' + placeholders + ')',
                    keyChunk
                );
            }

            const entries: any[][] = [];
            keyedItems.forEach((item) => {
                this.multiEntryValues(item, index).forEach((value) => {
                    entries.push([value, item[this.keyPath].toString()]);
                });
            });

            for(const entryChunk of this.splitInChunks(entries, 400)){
                if(entryChunk.length === 0){
                    continue;
                }

                const placeholders = entryChunk.map(() => '(?, ?)').join(',');
                const values = entryChunk.reduce((all, entry) => all.concat(entry), []);
                await DatabaseService.DB.executeSql(
                    'INSERT OR REPLACE INTO ' + this.table + '_' + index.name +
                    '(' + index.name + ', ' + foreignField + ') VALUES ' + placeholders,
                    values
                );
            }
        }
    }

    private async upsertObject(object: any, skipCollect = false): Promise<any>{
        const item = this.ensurePrimaryKey(skipCollect ? {...object} : this.collectObj({...object}));
        const hasPrimaryKey = this.hasValue(item[this.keyPath]);

        const simpleIndices = this.indices.filter((index) => !index.multiEntry);
        let parametros = hasPrimaryKey ? '?,?' : '?';
        let fields = hasPrimaryKey ? this.keyPath + ', value' : 'value';
        let values: any[] = hasPrimaryKey ? [item[this.keyPath], JSON.stringify(item)] : [JSON.stringify(item)];

        simpleIndices.forEach((index) => {
            const valor = this.getProperty(item, index.field);
            values.push(this.hasValue(valor) ? valor.toString() : (index.default ?? null));
            fields += ', ' + index.name;
            parametros += ',?';
        });

        const sql = 'INSERT OR REPLACE INTO ' + this.table + '(' + fields + ') VALUES (' + parametros + ')';
        const result = await DatabaseService.DB.executeSql(sql, values);

        if(!hasPrimaryKey){
            const key = result.insertId;
            item[this.keyPath] = key;
            await DatabaseService.DB.executeSql('UPDATE ' + this.table + ' SET value = ? WHERE ' + this.keyPath +' = ?', [JSON.stringify(item), key]);
        }

        await this.syncMultiEntryIndices([item]);

        return this.defineObj(item);
    }

    private async upsertArrayBatch(array: any[], skipCollect = false): Promise<any[]>{
        const rowsWithPrimaryKey: any[] = [];
        const withoutPrimaryKey: any[] = [];
        const result: any[] = [];

        for(const object of array){
            const item = this.ensurePrimaryKey(skipCollect ? {...object} : this.collectObj({...object}));

            if(!this.hasValue(item[this.keyPath])){
                withoutPrimaryKey.push(item);
                continue;
            }

            rowsWithPrimaryKey.push(item);
            result.push(this.defineObj(item));
        }

        if(rowsWithPrimaryKey.length > 0){
            await this.insertOrReplaceRows(rowsWithPrimaryKey);
            await this.syncMultiEntryIndices(rowsWithPrimaryKey);
        }

        for(const item of withoutPrimaryKey){
            result.push(await this.upsertObject(item, true));
        }

        return result;
    }

    private async insertOrReplaceRows(rows: any[]): Promise<void>{
        const fields = this.buildFields();
        const paramsPerRow = fields.length;
        const maxRowsByParams = Math.max(1, Math.floor(900 / paramsPerRow));
        const maxRowsPerStatement = Math.min(100, maxRowsByParams);
        const chunks = this.splitInChunks(rows, maxRowsPerStatement);

        for(const chunk of chunks){
            const placeholders: string[] = [];
            const values: any[] = [];

            for(const item of chunk){
                const value = JSON.stringify(item);
                placeholders.push('(' + fields.map(() => '?').join(',') + ')');
                values.push(...this.buildValues(item, value));
            }

            const sql = 'INSERT OR REPLACE INTO ' + this.table + '(' + fields.join(', ') + ') VALUES ' + placeholders.join(', ');
            await DatabaseService.DB.executeSql(sql, values);
        }
    }

    async paginate(filter: any = {}): Promise<any>{
        if(!this.initialized){
            await this.createTable();
            return this.paginate(filter);
        }

        filter = filter || {};

        const rawPagination = filter.pagination !== undefined && filter.pagination !== null ? filter.pagination : (filter.limit !== undefined && filter.limit !== null ? filter.limit : 20);

        filter.pagination = Number(rawPagination);
        filter.page = Number(filter.page || 1);

        /**
         * limit/pagination = 0 significa: buscar todos os registros.
         */
        const withoutPagination = filter.pagination === 0;

        if(!withoutPagination && (!filter.pagination || filter.pagination < 1)){
            filter.pagination = 20;
        }

        if(!filter.page || filter.page < 1){
            filter.page = 1;
        }

        const params: any[] = [];
        let sql = 'SELECT * FROM ' + this.table;

        if(filter.edificacao_id && this.indices.find((i) => i.field.indexOf('edificacao_id') != -1)){
            sql += ' WHERE by_edificacao = ' + filter.edificacao_id.toString();
        }

        sql = this.filterSql(sql, filter);

        if(filter.order && this.indices.find((i) => i.field.indexOf(filter.order.split(' ')[0]) != -1)){
            sql += ' ORDER BY ' + this.indices.find((i) => i.field.indexOf(filter.order.split(' ')[0]) != -1)?.name + ' ' + filter.order.split(' ')[1];
        }

        if(!withoutPagination){
            sql += ' LIMIT ? OFFSET ?';
            params.push(filter.pagination + 1);
            params.push((filter.page - 1) * filter.pagination);
        }

        const objects: any[] = [];
        const r = await DatabaseService.DB.executeSql(sql, params);

        const maxRows = withoutPagination ? r.rows.length : filter.pagination;

        for(let i = 0; i < r.rows.length && i < maxRows; i++){
            objects.push(this.defineObj(JSON.parse(r.rows.item(i).value)));
        }

        // const withObjects = await Promise.all(objects.map((o) => this.with(o)));
        const withObjects = await this.withBatch(objects);

        return {
            current_page: filter.page,
            data: withObjects,
            next_page_url: withoutPagination
                ? null
                : (r.rows.length > filter.pagination ? true : null)
        };
    }

    async put(object: any): Promise<any>{
        if(!this.initialized){
            await this.createTable();
            return this.put(object);
        }

        return this.upsertObject(object);
    }

    async putArray(array: any[], skipCollect = false): Promise<any>{
        if(!this.initialized){
            await this.createTable();
            return this.putArray(array, skipCollect);
        }

        if(!array || array.length === 0){
            return [];
        }

        const result: any[] = [];
        const chunks = this.splitInChunks(array, 200);

        for(const chunk of chunks){
            const saved = await this.upsertArrayBatch(chunk, skipCollect);
            result.push(...saved);
        }

        return result;
    }

    async sinc(object: any): Promise<any>{
        if(!this.initialized){
            await this.createTable();
            return this.sinc(object);
        }

        const prepared = this.prepareSyncArray([object]);

        if(prepared.length === 0){
            return null;
        }

        return this.upsertObject(prepared[0], true);
    }

    async sincArray(array: any[]): Promise<any>{
        if(!this.initialized){
            await this.createTable();
            return this.sincArray(array);
        }

        if(!array || array.length === 0){
            return [];
        }

        const prepared = this.prepareSyncArray(array);
        return this.putArray(prepared, true);
    }

    async sync(array: any[]): Promise<any>{
        if(!this.initialized){
            await this.createTable();
            return this.sync(array);
        }

        if(!array || array.length === 0){
            return [];
        }

        const prepared = this.prepareSyncArray(array);
        const toDelete = prepared.filter((item) => item.sync_status == 'delete' && this.hasValue(item[this.keyPath]));
        const toUpsert = prepared.filter((item) => item.sync_status != 'delete');

        if(toDelete.length > 0){
            await this.delArray(toDelete);
        }

        if(toUpsert.length === 0){
            return [];
        }

        return this.putArray(toUpsert, true);
    }

    async del(key: number | string, index: string | null = null): Promise<any>{
        if(!this.initialized){
            await this.createTable();
            return this.del(key, index);
        }

        for(const idx of this.indices){
            if(idx.multiEntry){
                await DatabaseService.DB.executeSql('DELETE FROM ' + this.table + '_' + idx.name + ' WHERE foreign_' + this.keyPath + ' = ?', [key.toString()]);
            }
        }

        const sql = 'DELETE FROM ' + this.table + ' WHERE ' + (index || this.keyPath) + ' = ?';
        return DatabaseService.DB.executeSql(sql, [key.toString()]);
    }

    async delArray(array: any[], index: string | null = null): Promise<any>{
        if(!this.initialized){
            await this.createTable();
            return this.delArray(array, index);
        }

        for(const obj of array){
            const key = index ? this.getProperty(obj, index) : obj[this.keyPath];

            if(this.hasValue(key)){
                await this.del(key, index);
            }
        }

        return true;
    }

    async clear(): Promise<any>{
        if(!this.initialized){
            await this.createTable();
            return this.clear();
        }

        for(const index of this.indices){
            if(index.multiEntry){
                await DatabaseService.DB.executeSql('DELETE FROM ' + this.table + '_' + index.name, []);
            }
        }

        return DatabaseService.DB.executeSql('DELETE FROM ' + this.table, []);
    }

    async count(): Promise<number>{
        if(!this.initialized){
            await this.createTable();
            return this.count();
        }

        const r = await DatabaseService.DB.executeSql('SELECT count(*) as contador FROM ' + this.table, []);
        return r.rows.item(0).contador;
    }

    protected filterSql(sql: string, params: any): string{
        return sql;
    }

    protected filterMultiEntry(sql: string, indexName: string, values: any[]): string{
        const index = this.indices.find((item) => item.name === indexName && item.multiEntry);
        const normalized = Array.from(new Set((values || [])
            .filter((value) => this.hasValue(value))
            .map((value) => value.toString())));

        if(!index || normalized.length === 0){
            return sql;
        }

        const escapedValues = normalized.map((value) => "'" + value.replace(/'/g, "''") + "'");
        const auxiliaryTable = this.table + '_' + index.name;
        const foreignField = 'foreign_' + this.keyPath;
        const clause = 'EXISTS (SELECT 1 FROM ' + auxiliaryTable + ' multi_entry' +
            ' WHERE multi_entry.' + foreignField + ' = ' + this.table + '.' + this.keyPath +
            ' AND multi_entry.' + index.name + ' IN (' + escapedValues.join(',') + '))';

        return sql + (sql.indexOf('WHERE') === -1 ? ' WHERE ' : ' AND ') + clause;
    }

    protected defineObj(obj: any){
        return obj;
    }

    protected collectObj(obj: any){
        obj = this.ensurePrimaryKey(obj);
        obj.created_at = obj.created_at || (moment as any)().format('YYYY-MM-DD HH:mm:ss');
        obj.updated_at = (moment as any)().format('YYYY-MM-DD HH:mm:ss');
        return obj;
    }

    private getProperty(obj: any, str: string): any {
        if(!str){
            return obj;
        }

        if(Array.isArray(obj)){
            return obj.reduce((values: any[], item: any) => {
                const value = this.getProperty(item, str);
                return values.concat(Array.isArray(value) ? value : [value]);
            }, []);
        }

        if(obj === undefined || obj === null){
            return undefined;
        }

        const parts = str.split('.');
        const value = obj[parts.shift() as string];

        return parts.length > 0 ? this.getProperty(value, parts.join('.')) : value;
    }
}

class LocalDB{
    constructor(private database: any){}

    async executeSql(statement: string, params = []){
        return new Promise((resolve, reject) => {
            this.database.transaction((transaction: any) => {
                transaction.executeSql(statement, params, function(t: any, r: any){
                    resolve(r);
                }, reject);
            });
        });
    }
}

class DataBaseLocal{
    transaction(t:any){
        return {
            executeSql: (a: string, b: any, c: any) => {
                c(null, {rows: {length: 0, item: () => null}});
            }
        }
    }
}
